import React, { useEffect, useState } from 'react';
import { DropdownOption, DropdownMenu, KebabMenu, Panel } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../../../overmind';
import { SkillDropdownOption, ICourseMappingCourseSkillConditionProps } from '../course-mapping-course.models';
import { CourseMappingResponseSkill } from '../../../../../models/overmind/learning-management';
import { FetchType } from '../../../../../enums';
import { createLevelsArray } from '../../../../../library/helpers/create-levels-array';
import { EntitiesCardWidget } from '../../../../../components/cards/entities-card.widget';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { ExplainerFocusPanelLayout } from '../../../../../widgets/layouts';
import { DisplayList } from '../../../../../components/lists/display-list';
import { ListItem } from '../../../../../components/lists/list-item';
import { CompanySkillListItemResponse } from '../../../../../models/overmind/company-entity';
import { SearchRolesRequest } from '../../../../../models/overmind/search-params';

export function CourseMappingCourseSkillCondition({ skills, onClickAddSkill, onClickRemoveSkill, onClickAddLevel, onClickRemoveLevel }: ICourseMappingCourseSkillConditionProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const defaultRequest: SearchRolesRequest = {
    search: undefined,
    sortAscending: true,
    pageSize: 99999,
    page: 1,
    companySlug: companyVariables.slug,
    searchGlobally: false,
  };

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [skillToView, setSkillToView] = useState<CourseMappingResponseSkill>();

  useEffect(() => {
    function onClickDropdownOption(skill: CompanySkillListItemResponse) {
      setSelectedOptions(currentState => {
        const nextState = structuredClone(currentState);
        const index = nextState.indexOf(skill.slug!);
        if (index === -1) {
          nextState.push(skill.slug!);
          onClickAddSkill(skill);
        } else {
          nextState.splice(index, 1);
          onClickRemoveSkill(skill.slug!);
        }
        return nextState;
      });
    }

    async function getDropdownOptions() {
      const response = await actions.getCompanySkills(defaultRequest);
      const options: SkillDropdownOption[] =
        response?.skills.map(skill => ({
          value: skill.slug!,
          label: skill.name,
          description: skill.description,
          onClick: () => onClickDropdownOption(skill),
          selected: skills?.some(temp => temp.slug === skill.slug),
        })) ?? [];
      setOptions(options);
    }

    async function getData() {
      actions.startLoader({ path: PagePath.learningManagementCourseMappingCourse, type: FetchType.PageFetching });
      await getDropdownOptions();
      actions.stopLoader(PagePath.learningManagementCourseMappingCourse);
    }

    getData();
  }, [companyVariables.slug]);

  useEffect(() => {
    const selectedSkills = skills?.map(skill => skill.slug!) ?? [];
    setSelectedOptions(selectedSkills);
  }, [skills, options]);

  return (
    <React.Fragment>
      <EntitiesCardWidget title="Skills" description="Courses assigned by skill will only appear once an assessment has been completed.">
        <DisplayList dropdownValue={selectedOptions} dropdownOptions={options}>
          {skills?.map(skill => {
            return (
              <ListItem key={skill.slug}>
                <div style={{ display: 'grid', gap: 15, alignItems: 'center', gridTemplateColumns: 'auto 1fr 195px' }}>
                  <KebabMenu
                    items={[
                      {
                        label: 'Remove skill',
                        onClick: () => {
                          onClickRemoveSkill(skill.slug!);
                          setSelectedOptions(currentState => {
                            const nextState = structuredClone(currentState);
                            const index = nextState.indexOf(skill.slug!);
                            index !== -1 ? nextState.push(skill.slug!) : nextState.splice(index, 1);
                            return nextState;
                          });
                        },
                      },
                      {
                        label: 'View skill',
                        onClick: () => setSkillToView(skill),
                      },
                    ]}
                  />

                  <div>
                    <div className="body" style={{ color: 'var(--accent-2)' }}>
                      {skill.name}
                    </div>

                    <small>{skill.description ?? 'No description provided'}</small>
                  </div>

                  <DropdownMenu
                    label="Expected proficiency"
                    options={createLevelsArray(companyVariables.maxLevel, companyVariables.minLevel).map(level => ({
                      value: level.toString(),
                      label: `Level ${level}`,
                      onClick: async () => {
                        skill.levels?.includes(level) ? onClickRemoveLevel(skill.slug!, level) : onClickAddLevel(skill.slug!, level);
                      },
                    }))}
                    value={skill.levels?.map(level => level.toString())}
                    responsive
                    multiple
                  />
                </div>
              </ListItem>
            );
          })}
        </DisplayList>
      </EntitiesCardWidget>

      <Panel open={!!skillToView} onClose={() => setSkillToView(undefined)}>
        {!!skillToView && (
          <ExplainerFocusPanelLayout supertitle="Learning Management" title={skillToView.name} onClose={() => setSkillToView(undefined)}>
            {skillToView.description}
          </ExplainerFocusPanelLayout>
        )}
      </Panel>
    </React.Fragment>
  );
}
